import { useCallback, useEffect, useRef } from 'react';

import './Wheel.css';

export interface WheelItem {
  value: string;
  label: string;
  /** Второстепенная подпись под основной — например, дата под днём недели. */
  note?: string;
}

/**
 * Барабан выбора в духе колеса iOS.
 *
 * Прокрутка нативная: `scroll-snap` сам доводит до ближайшего деления,
 * поэтому колесо одинаково работает пальцем, колесом мыши и трекпадом,
 * без обработки жестов вручную. Выбранным считается элемент, оказавшийся
 * в центре, — его позиция вычисляется из scrollTop, а не из событий,
 * что переживает и инерционную прокрутку iOS.
 *
 * Флага «программной прокрутки» здесь намеренно нет: он делал колесо
 * глухим на доли секунды после каждого выбора, а нужды в нём нет —
 * повторный выбор того же деления и так отсекается сравнением значений.
 */
export function Wheel({
  items,
  value,
  onChange,
  label,
  itemHeight = 40,
  visible = 5,
}: {
  items: WheelItem[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  itemHeight?: number;
  /** Сколько делений видно одновременно. Нечётное — чтобы был центр. */
  visible?: number;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const settleRef = useRef<number>(0);

  const index = Math.max(
    0,
    items.findIndex((item) => item.value === value),
  );

  /** Ставит колесо на нужное деление. */
  const scrollTo = useCallback(
    (target: number, smooth: boolean) => {
      listRef.current?.scrollTo({
        top: target * itemHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    },
    [itemHeight],
  );

  // Подкручиваем только при настоящем расхождении: если деление уже
  // под указателем, трогать прокрутку нельзя — это сбивает жест.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (Math.round(list.scrollTop / itemHeight) === index) return;
    scrollTo(index, false);
  }, [index, itemHeight, scrollTo]);

  function handleScroll() {
    // Реагируем на остановку, а не на каждый кадр прокрутки: промежуточные
    // деления при инерции выбором не считаются.
    window.clearTimeout(settleRef.current);
    settleRef.current = window.setTimeout(() => {
      const list = listRef.current;
      if (!list) return;

      const nearest = Math.min(
        items.length - 1,
        Math.max(0, Math.round(list.scrollTop / itemHeight)),
      );
      const item = items[nearest];
      if (item && item.value !== value) onChange(item.value);
    }, 80);
  }

  function handleKey(event: React.KeyboardEvent) {
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;

    event.preventDefault();
    const next = items[Math.min(items.length - 1, Math.max(0, index + step))];
    if (next) {
      onChange(next.value);
      scrollTo(items.indexOf(next), true);
    }
  }

  useEffect(() => () => window.clearTimeout(settleRef.current), []);

  const pad = ((visible - 1) / 2) * itemHeight;

  return (
    <div
      className="wheel"
      style={
        {
          '--item-h': `${itemHeight}px`,
          '--visible': visible,
        } as React.CSSProperties
      }
    >
      {/* Подсветка центрального деления — на ней «останавливается» выбор. */}
      <div className="wheel__pointer" aria-hidden />

      <div
        ref={listRef}
        className="wheel__list"
        role="listbox"
        aria-label={label}
        tabIndex={0}
        onScroll={handleScroll}
        onKeyDown={handleKey}
      >
        <div style={{ height: pad }} aria-hidden />

        {items.map((item, i) => (
          <div
            key={item.value}
            role="option"
            aria-selected={i === index}
            className={`wheel__item ${i === index ? 'wheel__item--on' : ''}`}
            onClick={() => {
              onChange(item.value);
              scrollTo(i, true);
            }}
          >
            <span className="wheel__label">{item.label}</span>
            {item.note && <span className="wheel__note">{item.note}</span>}
          </div>
        ))}

        <div style={{ height: pad }} aria-hidden />
      </div>
    </div>
  );
}
